import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { handleErrorMessage } from 'v3/utils/errorHandler';

import SidebarBox from 'v3/components/SidebarBox';

import * as S from './styles';
import { useParams, useNavigate } from 'react-router-dom';
// import { useIntercom } from 'hooks/useIntercom';
import PaymentMethod from 'components/PaymentMethod';
import { ACHForm } from './components/ach';
import { CCForm } from './components/credit-card';
import { useQuery } from 'v3/hooks/useQuery';
import { toast } from 'react-toastify';

import { setLeadData } from 'leadData/leadSlice';
import LoadingOverlay from './components/LoadingOverlay';
import * as Sentry from "@sentry/react";

import { PaymentClient } from 'v3/api/client/PaymentClient';

import Layout from 'v3/templates/Layout';
import { Row } from 'antd';
import { ContractFlowClient } from 'v3/api/client/ContractFlowClient';

import currency from 'currency.js';

const ChangePaymentMethod = () => {
  const { cfUuid } = useParams();
  const query = useQuery();
  const embedded = query.get('embedded') === 'true';
  const { leadData } = useSelector((state: RootState) => state.lead);
  const navigate = useNavigate();

  const [paymentMethod, setPaymentMethod] =
    useState<'ach' | 'credit-card'>('credit-card');

  const [isLoading, setIsLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [submitPaymentError, setSubmitPaymentError] = useState<boolean>(false);
  const dispatch = useDispatch();

  const ChoosePaymentMethod = () => {

    return (
      <>
        {isLoading && <LoadingOverlay />}
        <S.PaymentMethodSelection>
          <PaymentMethod
            title="Credit Card"
            type="credit-card"
            selected={paymentMethod === 'credit-card'}
            onClick={() => setPaymentMethod('credit-card')}
            />

          <PaymentMethod
            title="ACH"
            type="ach"
            selected={paymentMethod === 'ach'}
            onClick={() => setPaymentMethod('ach')}
            />
        </S.PaymentMethodSelection>
      </>
    );
  };

  useEffect(() => {
    async function fetchContractFlowInfo() {
      setIsLoading(true);
      const [data, error] = await ContractFlowClient().getContractFlowInfo(cfUuid);
      setIsLoading(false);
      // A failed load leaves no data to read: report it and stop, as App.js does.
      if (!data) {
        handleErrorMessage(error);
        return;
      }

      const { contractFlowStep, ...leadData } = data;
      dispatch(setLeadData({ currentStep: contractFlowStep, leadData }));
    }

    if (cfUuid) {
      fetchContractFlowInfo();
    }
  }, [cfUuid]);

  useEffect(() => {
      async function setupPaymentIntent() {
        if (!cfUuid) return;

        const {data, error} = await PaymentClient().setupPaymentIntent(cfUuid);
        if (error) {
          toast.error(`Error setting up payment intent`);
          console.error('Error setting up payment intent:', error);
          alert(JSON.stringify(error));
          return;
        }

        setClientSecret(data?.clientSecret || '');
      }
      setupPaymentIntent();
  }, [cfUuid, submitPaymentError]);

  const submitPaymentData = useCallback(async (
    paymentParams: {
      cardIdentifier?: string;
      plaidAccountId?: string;
      plaidPublicToken?: string
    }
  ) => {
    if (cfUuid) {
      setIsLoading(true);
      const { data, error } = await PaymentClient().setPaymentData(
        cfUuid,
        paymentParams,
      );

      if(error) {
        setIsLoading(false)
        setSubmitPaymentError(true);
        let errorMessage = error?.detail as string || "";

        if (errorMessage.includes("Request")) { // Stripe payment error
          errorMessage = errorMessage.replace(/Request.+: /g, "");
          toast.error(errorMessage);
        }
        else {
          Sentry.setContext("Contract Data", leadData);
          Sentry.captureException(error);
          console.error('Error callback submitting payment data:', error);

          toast.error("Error submitting payment data. Please try again.");
        }

        throw new Error(`Error submitting payment data: ${error as string}`);
      }

      if (data) {
        setIsLoading(false);
        setSubmitPaymentError(false);

        // Navigate to success page
        const embeddedParam = embedded ? '?embedded=true' : '';
        navigate(`/change-payment-method/${cfUuid}/success${embeddedParam}`);
      }
    }
  }, [cfUuid, embedded, navigate])

  const ACHFormSubmit = () => {
    if (!cfUuid) return null;
    return (
      <ACHForm
        cfUuid={cfUuid}
        submitPaymentData={submitPaymentData}
      />
    );
  };

  const CCFormSubmit = () => {
    return (
      <CCForm
        submitPaymentData={submitPaymentData}
        clientSecret={clientSecret}
        customerData={leadData}
      />
    );
  };

  const MembershipInfo = () => {

    const paymentMethodMap = {
      bank_account: 'Bank Account',
      credit_card: 'Credit Card',
    }

    const [paymentMethod, setPaymentMethod] = useState<string>('');

    useEffect(() => {
      if (leadData?.defaultPaymentSource) {
        const methodType = paymentMethodMap[leadData.defaultPaymentSource as keyof typeof paymentMethodMap];
        setPaymentMethod(`${methodType}: ${leadData?.paymentMethodLast4}` || '');
      }
    }, [leadData]);

    return (
      <>
        {embedded ? (
          <>
            <S.SectionTitleCompact>Current Payment Method</S.SectionTitleCompact>
            <S.P>
              <S.P>{paymentMethod}</S.P>
            </S.P>
          </>
        ) : (
          <>
            <S.SectionTitleCompact>Membership</S.SectionTitleCompact>
            <S.P>{leadData?.firstName} {leadData?.lastName} &lt;{leadData?.email}&gt;</S.P>
            <S.P>Club Value: <strong>{currency(leadData?.dealValue, { precision: 0 }).format()}</strong></S.P>
          </>
        )}
      </>
    );
  };

  return (
    <Layout hideHeader={embedded} hasHeaderLinks={false}>
        <Row justify="center">
          <S.Col xs={24} lg={12} xl={12}>
            {!embedded && <S.Title>Change Payment Method</S.Title>}

            <SidebarBox variant="secondary">
              <S.MembershipSection>
                  {isLoading ? (
                    <S.P>Loading payment data...</S.P>
                  ) : (
                    <MembershipInfo />
                  )}
              </S.MembershipSection>

              <S.SectionTitle>Payment Method</S.SectionTitle>

              <S.PaymentMethodSection>
                <S.ChoosePaymentMethodWrapper>
                  <S.ChoosePaymentMethodSection>
                    <ChoosePaymentMethod />
                  </S.ChoosePaymentMethodSection>
                  {paymentMethod === 'ach' ? <ACHFormSubmit /> : <CCFormSubmit />}
                </S.ChoosePaymentMethodWrapper>
              </S.PaymentMethodSection>
            </SidebarBox>
          </S.Col>
        </Row>
    </Layout>

  );
};

export default ChangePaymentMethod;
